import React from 'react';
import styled from 'styled-components';

import Button from '../../../Button';
import NoteForm, { StyledTextarea } from '../NoteForm';
import OnClickOutside from '../../../OnClickOutside';
import prepareTextWithLinks from '../../../../utils/prepareTextWithLinks';
import prepareTextWithNewline from '../../../../utils/prepareTextWithNewline';

import { Edit as EditIcon } from '@icons/common';
import { INoteAttachment } from '@components/CRM/Logs/Logs/interfaces/INote';
import { Attachments } from '@components/OrderCancellation/components';
import AttachmentList2 from '@components/CRM/Chains/AttachmentList2/AttachmentList2';

interface NoteProps {
  className?: string;
  hovered?: boolean;
  id: string;
  placeholder: string | null;
  readOnly?: boolean;
  value: string | null;
  attachments: INoteAttachment[];
  onEdit: ({
    id,
    value,
  }: {
    id: NoteProps['id'];
    value: NoteProps['value'];
  }) => void;
  onToggleEdit?: () => void;
  onDeleteAttachment?: (params: { attachId: string; noteId: string }) => void;
}

interface NoteState {
  isEditing: boolean;
}

class Note extends React.PureComponent<NoteProps, NoteState> {
  static defaultProps = {
    className: '',
    hovered: false,
    placeholder: null,
    readOnly: false,
    value: '',
  };

  constructor(props) {
    super(props);

    this.state = {
      isEditing: false,
    };

    this.handleClose = this.handleClose.bind(this);
    this.handleSave = this.handleSave.bind(this);
    this.toggleEdit = this.toggleEdit.bind(this);
    this.handleDeleteAttachment = this.handleDeleteAttachment.bind(this);
  }

  handleClose() {
    const { isEditing } = this.state;

    if (!isEditing) {
      return;
    }

    this.toggleEdit();
  }

  handleSave({ value }) {
    const { id, onEdit } = this.props;
    this.toggleEdit();
    onEdit({ id, value });
  }

  toggleEdit() {
    const { onToggleEdit } = this.props;
    this.setState(prevState => ({
      isEditing: !prevState.isEditing,
    }));
    onToggleEdit?.();
  }

  handleDeleteAttachment({ attachId }: { attachId?: string }) {
    const { id, onDeleteAttachment } = this.props;
    if (!attachId || !onDeleteAttachment) return;

    onDeleteAttachment({ attachId, noteId: id });
  }

  render() {
    const {
      hovered,
      placeholder,
      readOnly,
      value,
      attachments,
      onEdit,
      onToggleEdit,
      ...props
    } = this.props;
    const { isEditing } = this.state;

    return (
      <OnClickOutside onClickOutside={this.handleClose}>
        <StyledNote {...props} isEditing={isEditing}>
          <StyledNoteWrap isEditing={isEditing}>
            {isEditing ? (
              <StyledNoteForm
                value={value}
                isAllowedAttachmentsUploading={false}
                onCancel={this.toggleEdit}
                onSubmit={this.handleSave}
              />
            ) : (
              <StyledNoteText>
                {readOnly ? (
                  <span
                    dangerouslySetInnerHTML={{
                      __html: prepareTextWithNewline(
                        prepareTextWithLinks(value || '')
                      ),
                    }}
                  ></span>
                ) : (
                  <StyledEditableText
                    functional
                    title="Редактировать"
                    onClick={this.toggleEdit}
                  >
                    {value ? (
                      <>
                        <span
                          dangerouslySetInnerHTML={{
                            __html: prepareTextWithNewline(
                              prepareTextWithLinks(value)
                            ),
                          }}
                        ></span>
                        <StyledEditIcon visible={hovered} />
                      </>
                    ) : (
                      <StyledPlaceholder>{placeholder}</StyledPlaceholder>
                    )}
                  </StyledEditableText>
                )}
              </StyledNoteText>
            )}
          </StyledNoteWrap>
          {attachments?.length > 0 && (
            <StyledAttachments>
              <AttachmentList2
                list={attachments}
                isWithPreview
                onDelete={readOnly ? undefined : this.handleDeleteAttachment}
              />
            </StyledAttachments>
          )}
        </StyledNote>
      </OnClickOutside>
    );
  }
}

const StyledAttachments = styled.div`
  margin-top: 10px;
`;

const StyledNote = styled.div<{ isEditing: NoteState['isEditing'] }>`
  width: 100%;
  display: flex;
  flex-direction: column;

  ${({ isEditing }) =>
    isEditing &&
    `
    z-index: 3;
    position: relative;
  `}
`;

export const StyledPlaceholder = styled.span``;

const StyledEditableText = styled(Button)`
  text-align: left;
  width: calc(100% - 24px);
  path {
    stroke: #007aff;
  }
`;

const StyledEditIcon = styled(EditIcon)<{ visible: NoteProps['hovered'] }>`
  width: 14px;
  height: 15px;

  margin-left: 5px;
  margin-bottom: -4px;
  path {
    stroke: #919499;
  }

  ${({ visible }) =>
    visible &&
    `
    opacity: 1;
  `}

  :hover {
    path {
      stroke: #007aff;
    }
  }
`;

const StyledNoteText = styled.span`
  display: block;
  width: 100%;
  word-wrap: break-word;
`;

const StyledNoteForm = styled(NoteForm)`
  ${StyledTextarea} {
    padding: 0;
    border: none;
    height: auto;
    min-height: 20px;
  }
`;

const StyledNoteWrap = styled.div<{ isEditing: NoteState['isEditing'] }>`
  width: 100%;
  position: relative;

  ${({ isEditing }) =>
    isEditing &&
    `
    ${StyledNoteText} { opacity: 0; }
  `}
`;

export default Note;
